"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractDateAttributesFromFullDateStr = exports.fullDateStrToEpoch = exports.epochToFullDateStr = exports.epochToSlashedDateStrWithTime = exports.epochToSlashedDateStr = exports.epochToLocaleDateString = exports.optionalDateToFullDateStr = exports.dateToUTCFullDateString = exports.dateToFullDateString = exports.validateFullDateTimeStr = exports.validateFullDateStr = exports.strToFullDateStrOptional = exports.dateTimetoFullDateTimeStr = exports.strToDateTime = exports.strToFullDateStr = exports.optionalJSDateToUtcString = exports.datetimeToFullDateStr = void 0;
const lodash_1 = __importDefault(require("lodash"));
const luxon_1 = require("luxon");
const consts_1 = __importDefault(require("../consts"));
const error_1 = require("../error");
const typeguards_1 = __importDefault(require("../typeguards"));
const utils_1 = __importDefault(require("../utils"));
const consts_2 = require("./consts");
const { emptyNumberStringToNull } = utils_1.default;
/**
 * This file should contain all utilities related to date string manipulation
 * NOTE:
 * There are some functions suck as `*ToFullDateStr`, which may benefit from using function overloading.
 * I'm currently not overloading function for a few reasons:
 *  1. I don't know how to do this cleanly via arrow function in Typescript
 *  2. overloading can be confusing, if not used properly
 */
/**
 * converts Luxon DateTime object to finley standard Util.FullDateStr string format
 * @param val: input DateTime luxon object
 * @returns Util.FullDateStr
 */
const datetimeToFullDateStr = (val) => val.toFormat(consts_2.formatFullDateStr);
exports.datetimeToFullDateStr = datetimeToFullDateStr;
/**
 * converts Luxon DateTime object to datetime string
 * @param val: input DateTime luxon object
 * @returns string
 */
const optionalJSDateToUtcString = (val, fmt) => {
    if (lodash_1.default.isNil(val) || !(val instanceof Date)) {
        return '';
    }
    const dateTimeUtc = luxon_1.DateTime.fromJSDate(val).toUTC();
    if (!fmt) {
        // should be e.g. 2021-08-02T16:34:10.801Z
        return dateTimeUtc.toISO();
    }
    return dateTimeUtc.toFormat(fmt);
};
exports.optionalJSDateToUtcString = optionalJSDateToUtcString;
/**
 * converts date string in specified date string format to Util.FullDateStr
 * formats here: https://github.com/moment/luxon/blob/master/docs/formatting.md
 * @param val: input in String
 * @param fmt: dateformat in String
 * @returns Util.FullDateStr
 */
const strToFullDateStr = (val, fmt) => {
    const parsedDatetime = luxon_1.DateTime.fromFormat(val, fmt);
    if (!parsedDatetime.isValid) {
        throw new Error(`${parsedDatetime.invalidReason}: ${parsedDatetime.invalidExplanation}`);
    }
    return (0, exports.datetimeToFullDateStr)(parsedDatetime);
};
exports.strToFullDateStr = strToFullDateStr;
/**
 * converts date string in specified date string format to Util.FullDateStr
 * formats here: https://github.com/moment/luxon/blob/master/docs/formatting.md
 * @param val: input in String
 * @param fmt: dateformat in String
 * @returns DateTime
 */
const strToDateTime = (val, fmt, defaultTz) => {
    // https://moment.github.io/luxon/api-docs/index.html#datetimefromformat
    const options = {};
    if (defaultTz) {
        options.zone = defaultTz;
    }
    const parsedDatetime = luxon_1.DateTime.fromFormat(val, fmt, options);
    if (!parsedDatetime.isValid) {
        throw new Error(`${parsedDatetime.invalidReason}: ${parsedDatetime.invalidExplanation}`);
    }
    return parsedDatetime;
};
exports.strToDateTime = strToDateTime;
// convert to UTC ISO8601
const dateTimetoFullDateTimeStr = (val) => {
    // formats to ISO8601, e.g. '2017-04-20T11:32:00.000Z'
    const dateTimeStr = val.toUTC().toISO();
    (0, error_1.finleyAssert)(typeguards_1.default.isFullDateTimeStr(dateTimeStr), new error_1.FinleyError(`Invalid DateTime ISO string`));
    return dateTimeStr;
};
exports.dateTimetoFullDateTimeStr = dateTimetoFullDateTimeStr;
/**
 * converts date string in specified date string format to Util.FullDateStr,
 * returns null if input is not a valid FullDateStr
 * @param val: input in String | null
 * @param fmt: dateformat in String
 * @returns Util.FullDateStr
 */
const strToFullDateStrOptional = (val, fmt) => {
    if (lodash_1.default.isNil(emptyNumberStringToNull(val))) {
        return null;
    }
    try {
        return (0, exports.strToFullDateStr)(val, fmt);
    }
    catch (e) {
        return null;
    }
};
exports.strToFullDateStrOptional = strToFullDateStrOptional;
// TODO: IMO, this method probably can be improve in a few ways:
//  - Input `value` type to be string
//  - Use luxon datetime.isValid()
//  - It would also help to be more generic function like `isValidFullDateStr`
//  - all conversion function can convert and check for validity before returning
/**
 * validate that the GQL value is a valid date in YYYY-MM-DD format. if so, return that date string
 * inspired from https://github.com/Urigo/graphql-scalars
 */
const validateFullDateStr = (value) => {
    if (typeof value !== 'string') {
        throw new TypeError(`Value is not string: ${value}`);
    }
    // validate it's in the `YYYY-MM-DD` format
    const isValidFormat = consts_1.default.regExpFullDateStr.test(value);
    if (!isValidFormat) {
        throw new TypeError(`Value is not a valid Util.FullDateStr: ${value}`);
    }
    // validate that it is a valid date, e.g., not something like `2020-13-46`
    const valueAsDate = new Date(value);
    if (!lodash_1.default.isNumber(valueAsDate.getTime())) {
        throw new TypeError(`Value is not a valid Util.FullDateStr: ${value}`);
    }
    // some additional logic to catch invalid dates like `2020-02-30`
    // that we catch by serializing the Date object into an ISO string and checking that our serialized date matches
    // the original value
    const isCalendarDate = valueAsDate.toISOString() === `${value}T00:00:00.000Z`;
    if (!isCalendarDate) {
        throw new TypeError(`Value is not a valid Util.FullDateStr: ${value}`);
    }
    return value;
};
exports.validateFullDateStr = validateFullDateStr;
const validateFullDateTimeStr = (value) => {
    const isValidFullDateTimeStr = typeguards_1.default.isFullDateTimeStr(value);
    if (!isValidFullDateTimeStr) {
        throw new TypeError(`Value is not a valid Util.FullDateTimeStr: ${value}`);
    }
    return value;
};
exports.validateFullDateTimeStr = validateFullDateTimeStr;
/**
 * Convert from YYYY-MM-DD to other date string format (such as MM/dd/YYYY)
 */
const fullDateStrToStr = (val, toFmt) => {
    const parsedDatetime = luxon_1.DateTime.fromFormat(val, consts_2.formatFullDateStr);
    if (!parsedDatetime.isValid) {
        throw new Error(`${parsedDatetime.invalidReason}: ${parsedDatetime.invalidExplanation}`);
    }
    return parsedDatetime.toFormat(toFmt);
};
/**
 * converts date to ISO string, defaults zone to America/New_York
 */
const dateToIsoString = (date, zone = 'America/New_York') => {
    return luxon_1.DateTime.fromJSDate(date).setZone(zone).toISO();
};
/**
 * Converts JS Date to FullDateStr
 * @param date Date
 * @param zone <Optional> Defaults to `America/New_York`
 * @returns `FullDateStr`
 */
const dateToFullDateString = (date, zone = 'America/New_York') => {
    return luxon_1.DateTime.fromJSDate(date)
        .setZone(zone)
        .toFormat(consts_2.formatFullDateStr);
};
exports.dateToFullDateString = dateToFullDateString;
/**
 *
 * converts date to FullDateStr in UTC timezone
 */
const dateToUTCFullDateString = (date) => {
    return luxon_1.DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(consts_2.formatFullDateStr);
};
exports.dateToUTCFullDateString = dateToUTCFullDateString;
/**
 * If date exists, return FullDateStr
 * otherwise return undefined
 */
const optionalDateToFullDateStr = (date, zone) => {
    return date ? (0, exports.dateToFullDateString)(date, zone) : undefined;
};
exports.optionalDateToFullDateStr = optionalDateToFullDateStr;
/**
 * convert Date object to human friendly format. Date() -> `January 5, 2022`
 */
const dateToHumanDateString = (dateVal, locale = 'en-us', zone) => {
    return dateVal.toLocaleString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: zone,
    });
};
/**
 * convert Util.FullDateStr to human friendly format. 2022-01-05 -> `January 5, 2022`
 */
const fullDateStrToHumanDateString = (input, locale = 'en-us') => {
    const dt = luxon_1.DateTime.fromFormat(input, 'yyyy-MM-dd');
    return dateToHumanDateString(dt.toJSDate(), locale, locale === 'en-us' ? 'America/New_York' : undefined);
};
/**
 * convert Util.FullDateStr to human friendly format. 2022-01-05 -> `Jan 5`
 */
const fullDateStrToShortMonthDayString = (input, locale = 'en-us') => {
    const dt = luxon_1.DateTime.fromFormat(input, 'yyyy-MM-dd');
    return dt.toJSDate().toLocaleString(locale, {
        month: 'short',
        day: 'numeric',
    });
};
/**
 * Converts FullDateStr to ISO 8601 formatted timestamp
 *
 * Example
 * '1982-05-25' => '1982-05-25T00:00:00.000Z'
 */
const fullDateStrToIso = (date, zone = 'America/New_York') => {
    return luxon_1.DateTime.fromISO(date).setZone(zone).toISO();
};
const optionalFullDateStrToIso = (date, zone) => {
    return date ? fullDateStrToIso(date, zone) : undefined;
};
/**
 * Converts a date in millis to the local time zone specified. Defaults to EST.
 * @param dateInMillis the date in milliseconds
 * @param tz the Luxon timezone code
 * @returns local timezone date string
 */
const epochToLocaleDateString = (dateInMillis, tz = 'America/New_York', format = luxon_1.DateTime.DATETIME_FULL) => {
    return luxon_1.DateTime.fromMillis(dateInMillis).setZone(tz).toLocaleString(format);
};
exports.epochToLocaleDateString = epochToLocaleDateString;
/**
 * Converts a date in millis to SlashedDateStr format.
 * @param dateInMillis the date in milliseconds
 * @param tz the Luxon timezone code
 * @returns SlashedDateStr
 */
const epochToSlashedDateStr = (dateInMillis, tz = 'America/New_York') => {
    return luxon_1.DateTime.fromMillis(dateInMillis)
        .setZone(tz)
        .toFormat(consts_2.formatSlashedDateStr);
};
exports.epochToSlashedDateStr = epochToSlashedDateStr;
/**
 * Converts a date in millis to SlashedDateStr format.
 * @param dateInMillis the date in milliseconds
 * @param tz the Luxon timezone code
 * @returns SlashedDateStr
 */
const epochToSlashedDateStrWithTime = (dateInMillis, tz = 'America/New_York') => {
    return luxon_1.DateTime.fromMillis(dateInMillis)
        .setZone(tz)
        .toFormat('M/d/yy hh:mm a');
};
exports.epochToSlashedDateStrWithTime = epochToSlashedDateStrWithTime;
const epochToFullDateStr = (dateInMillis, tz = 'America/New_York') => {
    return (0, exports.datetimeToFullDateStr)(luxon_1.DateTime.fromMillis(dateInMillis).setZone(tz));
};
exports.epochToFullDateStr = epochToFullDateStr;
/**
 * converts fullDateStr to epoch, defaults timezone to EST
 */
const fullDateStrToEpoch = (fullDateStr, tz = 'America/New_York') => {
    return luxon_1.DateTime.fromISO(fullDateStr).setZone(tz).toMillis();
};
exports.fullDateStrToEpoch = fullDateStrToEpoch;
/**
 * Given a string representing a full date str in the format "YYYY-MM-DD"
 * return a string representing slashed date str specific type "M/d"
 * @param {FullDateStr} fullDateStr - Util.FullDateStr
 * @returns A string of the form "M/D/yy"
 */
const fullDateStrToSlashedDateStr = (fullDateStr, slashedDateFormat) => {
    return fullDateStrToStr(fullDateStr, slashedDateFormat);
};
/**
 * converts from YYYY-MM-DD to other date string format (such as MM/dd/YYYY)
 * but input value can be null (optional)
 */
const fullDateStrToStrOptional = (val, toFmt) => {
    if (lodash_1.default.isNil(val)) {
        return null;
    }
    return fullDateStrToStr(val, toFmt);
};
/**
 * Convert a fullDateStr to luxon DateTime object
 * @param fullDateStr date string in YYYY-MM-DD
 * @returns luxon DateTime object
 */
const fullDateStrToDateTime = (fullDateStr) => {
    (0, exports.validateFullDateStr)(fullDateStr);
    return luxon_1.DateTime.fromFormat(fullDateStr, consts_2.formatFullDateStr);
};
/**
 * Adds x amount of units from input FullDateString and returns new FullDateStr
 */
const offsetFromFullDateStr = (date, options) => {
    return luxon_1.DateTime.fromISO(date)
        .plus(options)
        .toFormat(consts_2.formatFullDateStr);
};
const optionalOffsetFromFullDateStr = (date, options) => {
    return date ? offsetFromFullDateStr(date, options) : undefined;
};
/**
 * Converts FullDateStr to Date
 * Use case: primarily in browser when accessing date picker initialized with FullDateStr
 * Warning: defaults zone to client/system timezone, be careful with timezones!
 */
const fullDateStrToDate = (date) => {
    const [year, month, day] = date.split('-');
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
};
/**
 * Returns { year, month, dayOfMonth } from FullDateStr
 */
const extractDateAttributesFromFullDateStr = (dateStr) => {
    const [year, month, dayOfMonth] = dateStr.split('-');
    return {
        year: parseInt(year, 10),
        month: parseInt(month, 10),
        dayOfMonth: parseInt(dayOfMonth, 10),
    };
};
exports.extractDateAttributesFromFullDateStr = extractDateAttributesFromFullDateStr;
/**
 * Gets the calendar quarter that a FullDateStr is in
 */
const fullDateStrToQuarter = (date) => {
    const jsDate = fullDateStrToDate(date);
    const quarter = Math.floor((jsDate.getMonth() + 3) / 3);
    return quarter;
};
/**
 * Gets the calendar week of year that a FullDateStr is in
 */
const fullDateStrToWeekNumber = (date) => {
    const jsDate = fullDateStrToDate(date);
    return luxon_1.DateTime.fromJSDate(jsDate).weekNumber;
};
/**
 * Export as default methods of `common.date` submodule
 */
exports.default = {
    optionalJSDateToUtcString: exports.optionalJSDateToUtcString,
    // optionalIsoTimestampToUtc,
    dateToHumanDateString,
    dateToIsoString,
    dateToFullDateString: exports.dateToFullDateString,
    dateToUTCFullDateString: exports.dateToUTCFullDateString,
    datetimeToFullDateStr: exports.datetimeToFullDateStr,
    dateTimetoFullDateTimeStr: exports.dateTimetoFullDateTimeStr,
    epochToLocaleDateString: exports.epochToLocaleDateString,
    epochToSlashedDateStr: exports.epochToSlashedDateStr,
    epochToSlashedDateStrWithTime: exports.epochToSlashedDateStrWithTime,
    epochToFullDateStr: exports.epochToFullDateStr,
    extractDateAttributesFromFullDateStr: exports.extractDateAttributesFromFullDateStr,
    fullDateStrToDate,
    fullDateStrToDateTime,
    fullDateStrToEpoch: exports.fullDateStrToEpoch,
    fullDateStrToHumanDateString,
    fullDateStrToShortMonthDayString,
    fullDateStrToIso,
    fullDateStrToQuarter,
    fullDateStrToWeekNumber,
    fullDateStrToSlashedDateStr,
    fullDateStrToStr,
    fullDateStrToStrOptional,
    offsetFromFullDateStr,
    optionalDateToFullDateStr: exports.optionalDateToFullDateStr,
    optionalFullDateStrToIso,
    optionalOffsetFromFullDateStr,
    strToDateTime: exports.strToDateTime,
    strToFullDateStr: exports.strToFullDateStr,
    strToFullDateStrOptional: exports.strToFullDateStrOptional,
    validateFullDateStr: exports.validateFullDateStr,
    validateFullDateTimeStr: exports.validateFullDateTimeStr,
};
//# sourceMappingURL=datestr.js.map